import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Row, Col, Card, Button, Modal, Form, ProgressBar, Pagination } from "react-bootstrap";
import {
  BsStarFill as StarFill,
  BsStarHalf as StarHalf,
  BsStar as StarEmpty,
  BsHandThumbsUp as ThumbsUp,
  BsHandThumbsUpFill as ThumbsUpFill,
  BsCheckCircleFill as CheckBadge,
  BsCloudUpload,
  BsX,
  BsSearch,
  BsZoomIn
} from "react-icons/bs";
import { SERVER_PATH } from "../../config/env";
import { successToaster, errorToaster } from "../../utils";
import { userInfo } from "../../utils/appInfo";
import "./customerReviews.css";

export default function CustomerReviews({ productId }) {
  // Rating Summary State
  const [summary, setSummary] = useState({
    average_rating: 0,
    total_reviews: 0,
    star_counts: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
  });

  // Reviews List State
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("newest");
  const [starFilter, setStarFilter] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Write Review Modal State
  const [showModal, setShowModal] = useState(false);
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formRating, setFormRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formFeedback, setFormFeedback] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Lightbox Modal State
  const [lightboxImage, setLightboxImage] = useState(null);

  // LocalStorage for Helpful Clicks
  const [helpfulClicked, setHelpfulClicked] = useState({});

  useEffect(() => {
    // Load helpful clicks from localStorage
    const saved = localStorage.getItem("vebbox_helpful_reviews") || localStorage.getItem("sivam_helpful_reviews");
    if (saved) {
      try {
        setHelpfulClicked(JSON.parse(saved));
      } catch (e) {}
    }
  }, []);

  // Auto prefill form name & email when user is logged in
  useEffect(() => {
    if (showModal) {
      const user = userInfo();
      if (user) {
        if (user.user_name && !formName) setFormName(user.user_name);
        if (user.email && !formEmail) setFormEmail(user.email);
      }
    }
  }, [showModal]);

  const fetchSummary = useCallback(() => {
    if (!productId) return;
    axios
      .get(`${SERVER_PATH}get/rating_summary.php`, { params: { product_id: productId } })
      .then((res) => {
        if (res.data) {
          setSummary({
            average_rating: Number(res.data.average_rating) || 0,
            total_reviews: Number(res.data.total_reviews) || 0,
            star_counts: res.data.star_counts || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
          });
        }
      })
      .catch((err) => console.log("Summary fetch error:", err));
  }, [productId]);

  const fetchReviews = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    axios
      .get(`${SERVER_PATH}get/reviews.php`, {
        params: {
          product_id: productId,
          sort: sortOption,
          rating: starFilter,
          page: currentPage,
          limit: 10,
        },
      })
      .then((res) => {
        if (res.data) {
          setReviews(res.data.reviews || []);
          setTotalPages(res.data.total_pages || 1);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log("Reviews fetch error:", err);
        setLoading(false);
      });
  }, [productId, sortOption, starFilter, currentPage]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Allowed formats: jpg, jpeg, png, webp
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      errorToaster({ msg: "Invalid image type. Only JPG, JPEG, PNG, and WEBP allowed." });
      return;
    }

    // Max size 2MB (2 * 1024 * 1024 bytes)
    if (file.size > 2 * 1024 * 1024) {
      errorToaster({ msg: "Image size must be less than 2MB." });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setImageBase64(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeImage = () => {
    setImagePreview(null);
    setImageBase64("");
  };

  const handleSubmitReview = (e) => {
    e.preventDefault();

    if (!formName.trim()) {
      errorToaster({ msg: "Please enter your name." });
      return;
    }

    if (!formEmail.trim() || !/\S+@\S+\.\S+/.test(formEmail)) {
      errorToaster({ msg: "Please enter a valid email address." });
      return;
    }

    if (!formRating || formRating < 1 || formRating > 5) {
      errorToaster({ msg: "Please select a rating between 1 and 5 stars." });
      return;
    }

    if (formFeedback.trim().length < 10) {
      errorToaster({ msg: "Feedback must be at least 10 characters." });
      return;
    }

    if (formFeedback.trim().length > 1000) {
      errorToaster({ msg: "Feedback cannot exceed 1000 characters." });
      return;
    }

    setSubmitting(true);

    axios
      .post(`${SERVER_PATH}post/review.php`, {
        product_id: productId,
        name: formName.trim(),
        email: formEmail.trim(),
        rating: formRating,
        feedback: formFeedback.trim(),
        image: imageBase64,
        verified_purchase: 0,
      })
      .then((res) => {
        setSubmitting(false);
        if (res.data.status === "success") {
          successToaster({ msg: "Thank you. Your review has been submitted for approval." });
          setShowModal(false);
          // Reset Form
          setFormName("");
          setFormEmail("");
          setFormRating(0);
          setFormFeedback("");
          setImagePreview(null);
          setImageBase64("");
        } else {
          errorToaster({ msg: res.data.message || "Failed to submit review." });
        }
      })
      .catch(() => {
        setSubmitting(false);
        errorToaster({ msg: "Something went wrong submitting your review." });
      });
  };

  const handleHelpfulClick = (reviewId) => {
    if (helpfulClicked[reviewId]) {
      return; // Already clicked
    }

    // Optimistic UI Update - Increment instantly & lock button
    const updatedClicks = { ...helpfulClicked, [reviewId]: true };
    setHelpfulClicked(updatedClicks);
    localStorage.setItem("vebbox_helpful_reviews", JSON.stringify(updatedClicks));

    setReviews((prev) =>
      prev.map((r) => (r.id === reviewId ? { ...r, helpful_count: Number(r.helpful_count) + 1 } : r))
    );

    axios
      .post(`${SERVER_PATH}post/helpful.php`, { review_id: reviewId })
      .catch(() => {});
  };

  const getCustomerInitial = (name) => {
    if (!name) return "C";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const renderStars = (rating, size = "1.1rem") => {
    const stars = [];
    const num = Number(rating);
    for (let i = 1; i <= 5; i++) {
      if (i <= num) {
        stars.push(<StarFill key={i} className="text-warning me-1" style={{ fontSize: size }} />);
      } else if (i - 0.5 <= num) {
        stars.push(<StarHalf key={i} className="text-warning me-1" style={{ fontSize: size }} />);
      } else {
        stars.push(<StarEmpty key={i} className="text-muted opacity-50 me-1" style={{ fontSize: size }} />);
      }
    }
    return stars;
  };

  const renderSkeleton = () => (
    <div className="reviews-skeleton d-flex flex-column gap-3">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="border-0 shadow-xs rounded-3 p-3 bg-white skeleton-card mb-2">
          <Card.Body className="p-2">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="d-flex align-items-center gap-3">
                <div className="skeleton-avatar bg-secondary bg-opacity-25" style={{ width: "45px", height: "45px" }}></div>
                <div>
                  <div className="skeleton-line bg-secondary bg-opacity-25 mb-2" style={{ width: "120px", height: "16px" }}></div>
                  <div className="skeleton-line bg-secondary bg-opacity-25" style={{ width: "80px", height: "12px" }}></div>
                </div>
              </div>
              <div className="skeleton-line bg-secondary bg-opacity-25" style={{ width: "90px", height: "18px" }}></div>
            </div>
            <div className="skeleton-line bg-secondary bg-opacity-25 mb-2" style={{ width: "100%", height: "14px" }}></div>
            <div className="skeleton-line bg-secondary bg-opacity-25" style={{ width: "70%", height: "14px" }}></div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="customer-reviews-section my-5 p-4 rounded-4 bg-white shadow-sm border border-light">
      <h3 className="section-title text-uppercase fw-bold text-success mb-4 d-flex align-items-center justify-content-between">
        <span>CUSTOMER REVIEWS</span>
        <Button variant="success" className="btn-write-review rounded-pill px-4 py-2 fw-semibold shadow-sm" onClick={() => setShowModal(true)}>
          WRITE A REVIEW
        </Button>
      </h3>

      {/* Overview & Rating Summary */}
      <Row className="g-4 mb-4 align-items-center">
        <Col md={4} className="text-center border-end-md">
          <div className="average-rating-num mb-1">{summary.average_rating ? summary.average_rating.toFixed(1) : "0.0"}</div>
          <div className="mb-2">{renderStars(summary.average_rating, "1.4rem")}</div>
          <div className="text-muted fw-semibold fs-6">
            Based on {summary.total_reviews} {summary.total_reviews === 1 ? "Review" : "Reviews"}
          </div>
        </Col>

        <Col md={8}>
          <div className="star-breakdown px-lg-3">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = summary.star_counts[star] || 0;
              const percent = summary.total_reviews > 0 ? (count / summary.total_reviews) * 100 : 0;
              const isSelected = starFilter === star;
              return (
                <div
                  key={star}
                  className={`star-breakdown-row d-flex align-items-center mb-2 ${isSelected ? "active-star-row" : ""}`}
                  onClick={() => {
                    setStarFilter(isSelected ? 0 : star);
                    setCurrentPage(1);
                  }}
                  title={`Filter by ${star} star reviews`}
                >
                  <span className={`me-2 fw-semibold small style-star-label ${isSelected ? "text-success fw-bold" : "text-secondary"}`} style={{ width: "40px" }}>
                    {star} ★
                  </span>
                  <div className="flex-grow-1 mx-2" style={{ height: "10px" }}>
                    <ProgressBar
                      now={percent}
                      variant="success"
                      className="h-100 rounded-pill bg-light"
                    />
                  </div>
                  <span className={`ms-2 small style-star-count ${isSelected ? "text-success fw-bold" : "text-muted"}`} style={{ width: "45px", textAlign: "right" }}>
                    ({count})
                  </span>
                </div>
              );
            })}
          </div>
        </Col>
      </Row>

      {/* Filter and Sort Controls */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 border-top border-bottom py-3 mb-4">
        {/* Filter Buttons */}
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <span className="fw-semibold text-secondary small me-1">Filter Stars:</span>
          <Button
            size="sm"
            variant={starFilter === 0 ? "success" : "outline-secondary"}
            className={`rounded-pill px-3 filter-btn-pill ${starFilter === 0 ? "active" : ""}`}
            onClick={() => { setStarFilter(0); setCurrentPage(1); }}
          >
            All
          </Button>
          {[5, 4, 3, 2, 1].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={starFilter === s ? "success" : "outline-secondary"}
              className={`rounded-pill px-3 filter-btn-pill ${starFilter === s ? "active" : ""}`}
              onClick={() => { setStarFilter(s); setCurrentPage(1); }}
            >
              {s} ★
            </Button>
          ))}
        </div>

        {/* Sorting Dropdown */}
        <div className="d-flex align-items-center gap-2">
          <span className="fw-semibold text-secondary small">Sort By:</span>
          <Form.Select
            size="sm"
            value={sortOption}
            onChange={(e) => { setSortOption(e.target.value); setCurrentPage(1); }}
            className="rounded-pill border-secondary border-opacity-25 px-3 py-1 shadow-xs"
            style={{ width: "170px" }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
            <option value="helpful">Most Helpful</option>
          </Form.Select>
        </div>
      </div>

      {/* Reviews List */}
      {loading ? (
        renderSkeleton()
      ) : summary.total_reviews === 0 ? (
        <div className="empty-reviews-state text-center py-5 px-3 bg-light bg-opacity-50 rounded-4 border border-dashed border-secondary border-opacity-25 my-3">
          <StarEmpty className="display-4 text-warning opacity-50 mb-3" />
          <h4 className="fw-bold text-dark mb-2">No Reviews Yet</h4>
          <p className="text-secondary mb-4 max-w-md mx-auto">Be the first customer to write a review for this product!</p>
          <Button variant="success" className="btn-write-review rounded-pill px-5 py-2 fw-semibold shadow-sm" onClick={() => setShowModal(true)}>
            Write a Review
          </Button>
        </div>
      ) : reviews.length === 0 ? (
        <div className="empty-filter-state text-center py-5 px-3 bg-light rounded-4 border my-3">
          <h5 className="fw-bold text-dark mb-2">No {starFilter}★ Reviews Found</h5>
          <p className="text-muted mb-3">There are no approved reviews matching this star filter.</p>
          <Button variant="outline-success" className="rounded-pill px-4" onClick={() => { setStarFilter(0); setCurrentPage(1); }}>
            Show All Approved Reviews
          </Button>
        </div>
      ) : (
        <div className="reviews-grid d-flex flex-column gap-3">
          {reviews.map((rev) => {
            const fullImgUrl = rev.image ? (rev.image.startsWith("http") ? rev.image : `${SERVER_PATH.replace('controllers/api/', '')}${rev.image}`) : null;
            return (
              <Card key={rev.id} className="review-card border-0 p-3">
                <Card.Body className="p-2">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-3">
                      <div className="customer-avatar-circle rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: "45px", height: "45px", fontSize: "1.1rem" }}>
                        {getCustomerInitial(rev.name)}
                      </div>
                      <div>
                        <div className="fw-bold text-dark d-flex align-items-center gap-2">
                          {rev.name}
                          {Number(rev.verified_purchase) === 1 && (
                            <span className="badge badge-verified-purchase rounded-pill px-2 py-1 d-inline-flex align-items-center">
                              <CheckBadge className="me-1" /> Verified Purchase
                            </span>
                          )}
                        </div>
                        <small className="text-muted">
                          {new Date(rev.created_at).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </small>
                      </div>
                    </div>
                    <div>{renderStars(rev.rating, "1.1rem")}</div>
                  </div>

                  {/* Feedback Text */}
                  <p className="review-text text-secondary mt-2 mb-3" style={{ whiteSpace: "pre-line" }}>
                    {rev.feedback}
                  </p>

                  {/* Uploaded Review Image */}
                  {fullImgUrl && (
                    <div className="mb-3">
                      <div className="review-img-container shadow-xs" onClick={() => setLightboxImage(fullImgUrl)}>
                        <img
                          src={fullImgUrl}
                          alt="Customer review photo"
                          className="review-uploaded-img rounded-3 border"
                        />
                        <div className="review-img-overlay">
                          <BsZoomIn className="fs-4" />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Helpful Button */}
                  <div className="d-flex align-items-center justify-content-end pt-2 border-top border-secondary border-opacity-10">
                    <Button
                      size="sm"
                      variant={helpfulClicked[rev.id] ? "success" : "outline-secondary"}
                      className="rounded-pill px-3 py-1 btn-helpful d-inline-flex align-items-center gap-1"
                      onClick={() => handleHelpfulClick(rev.id)}
                      disabled={helpfulClicked[rev.id]}
                    >
                      {helpfulClicked[rev.id] ? <ThumbsUpFill /> : <ThumbsUp />}
                      <span>Helpful</span>
                      {Number(rev.helpful_count) > 0 && <span className="fw-bold ms-1">({rev.helpful_count})</span>}
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination className="shadow-xs rounded-pill">
            <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} />
          </Pagination>
        </div>
      )}

      {/* WRITE A REVIEW MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg" className="review-modal">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold text-success text-uppercase">WRITE A REVIEW</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form onSubmit={handleSubmitReview}>
            {/* Rating Stars Selection */}
            <Form.Group className="mb-4 text-center">
              <Form.Label className="fw-semibold text-secondary d-block mb-2">Overall Rating *</Form.Label>
              <div className="interactive-stars d-inline-flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <span
                    key={star}
                    style={{ cursor: "pointer", fontSize: "2.4rem" }}
                    className={`star-icon ${star <= (hoverRating || formRating) ? "text-warning" : "text-muted opacity-25"}`}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setFormRating(star)}
                  >
                    ★
                  </span>
                ))}
              </div>
              {formRating > 0 && (
                <div className="small text-success fw-semibold mt-1">
                  {formRating === 5 && "5 ★ - Excellent! Loved it."}
                  {formRating === 4 && "4 ★ - Good! Pretty satisfied."}
                  {formRating === 3 && "3 ★ - Average. It was okay."}
                  {formRating === 2 && "2 ★ - Poor. Could be better."}
                  {formRating === 1 && "1 ★ - Terrible. Not satisfied."}
                </div>
              )}
            </Form.Group>

            <Row className="g-3 mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small text-secondary">Your Name *</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter your full name"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold small text-secondary">Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={formEmail}
                    onChange={(e) => setFormEmail(e.target.value)}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Feedback textarea with counter */}
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <Form.Label className="fw-semibold small text-secondary mb-0">Your Review *</Form.Label>
                <span className={`small ${formFeedback.length > 1000 ? "text-danger fw-bold" : "text-muted"}`}>
                  {formFeedback.length} / 1000
                </span>
              </div>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Share details of your experience with this product... (min 10 characters)"
                value={formFeedback}
                onChange={(e) => setFormFeedback(e.target.value)}
                maxLength={1000}
                required
              />
            </Form.Group>

            {/* Upload Image */}
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold small text-secondary">Upload Product Photo (Optional, Max 2MB)</Form.Label>
              <div className="upload-box p-3 rounded-3 border border-dashed text-center bg-light">
                {imagePreview ? (
                  <div className="position-relative d-inline-block">
                    <img src={imagePreview} alt="Preview" className="rounded-3 border" style={{ width: "120px", height: "120px", objectFit: "cover" }} />
                    <Button
                      variant="danger"
                      size="sm"
                      className="position-absolute top-0 end-0 rounded-circle p-1 shadow-sm"
                      onClick={removeImage}
                      style={{ transform: "translate(40%, -40%)" }}
                    >
                      <BsX className="fs-5" />
                    </Button>
                  </div>
                ) : (
                  <label style={{ cursor: "pointer" }} className="w-100 m-0 py-2">
                    <BsCloudUpload className="display-6 text-success mb-2" />
                    <div className="small fw-semibold text-secondary">Click to upload photo</div>
                    <div className="small text-muted">Formats: JPG, JPEG, PNG, WEBP</div>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={handleImageChange} className="d-none" />
                  </label>
                )}
              </div>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 pt-2 border-top">
              <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button variant="success" type="submit" className="rounded-pill px-5 fw-semibold" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Review"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Lightbox Modal */}
      <Modal show={!!lightboxImage} onHide={() => setLightboxImage(null)} centered size="lg" className="lightbox-modal">
        <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
        <Modal.Body className="text-center p-3 bg-black rounded-bottom">
          <img src={lightboxImage} alt="Review Image Lightbox" className="img-fluid rounded shadow" style={{ maxHeight: "80vh" }} />
        </Modal.Body>
      </Modal>
    </div>
  );
}
